import type { TreeOption } from 'naive-ui';
import { isAssemblyMateDiagnosticError } from '@puppycad/cad-core';
import type { AssemblyMateDocumentModel } from '@puppycad/cad-core';
import { FeatureTreeNodeKind } from '@/hooks/feature-tree/feature-tree-types';
import type { FeatureTreeNodeMeta } from '@/hooks/feature-tree/feature-tree-types';
import type { FeatureTreePresentationIndex } from './FeatureTreePresentationIndex';

/**
 * 按 mateId 索引当前树中的所有出现项，仅同步诊断字段，不重建结构或解释组件身份。
 *
 * 与激活树/待提交树分别绑定；结构发布和文档释放时由所有者 clear，禁止跨文档持有旧节点。
 */
export class AssemblyMateDiagnosticPresentationIndex {
  /** 当前索引的树和元数据只用于结构身份判断。 */
  private tree: readonly TreeOption[] | null = null;
  private nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta> | null = null;
  /** 一个 Mate 同时可能出现在主文件夹和多个组件引用文件夹中。 */
  private readonly bindingsByMateId = new Map<
    string,
    AssemblyMateDiagnosticTreeBinding[]
  >();
  /** 显式观测统计，不在更新循环分配统计对象。 */
  private nodes = 0;
  private builds = 0;
  private changes = 0;

  /** @param nodeIndex 共享的 UI 节点定位端口，不额外遍历对象树。 */
  public constructor(
    private readonly nodeIndex: Pick<FeatureTreePresentationIndex, 'resolve'>,
  ) {}

  /**
   * 应用权威文档诊断，状态相同的节点不写入响应式数据。
   * @param tree 当前纯数据结构树。
   * @param nodeMap 与当前树配对的元数据。
   * @param document 诊断变化后的权威 Mate 文档；不从此入口增删树节点。
   */
  public apply(
    tree: readonly TreeOption[],
    nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
    document: AssemblyMateDocumentModel,
  ): void {
    if (this.tree !== tree || this.nodeMap !== nodeMap)
      this.rebuild(tree, nodeMap);
    for (const mate of document.mates) {
      const bindings = this.bindingsByMateId.get(mate.mateId);
      if (!bindings) continue;
      const statusError = isAssemblyMateDiagnosticError(
        mate.solveStatus,
        mate.errorMessage,
      );
      for (const { node, metadata } of bindings) {
        if (
          node.statusError === statusError &&
          node.mateSolveStatus === mate.solveStatus &&
          node.mateErrorMessage === mate.errorMessage &&
          metadata.mateSolveStatus === mate.solveStatus &&
          metadata.mateErrorMessage === mate.errorMessage
        )
          continue;
        // 徽标和提示依赖同一响应式节点；仅修改普通元数据 Map 不会触发提示文本重绘。
        node.statusError = statusError;
        node.mateSolveStatus = mate.solveStatus;
        node.mateErrorMessage = mate.errorMessage;
        metadata.mateSolveStatus = mate.solveStatus;
        metadata.mateErrorMessage = mate.errorMessage;
        this.changes++;
      }
    }
  }

  /** @returns 当前绑定规模与实际更新计数。 */
  public getStats(): AssemblyMateDiagnosticTreeStats {
    return { nodes: this.nodes, builds: this.builds, changes: this.changes };
  }

  /** 解除旧投影引用；共享节点索引仍由宿主拥有。 */
  public clear(): void {
    this.tree = null;
    this.nodeMap = null;
    this.bindingsByMateId.clear();
    this.nodes = 0;
  }

  /**
   * 仅在结构发布后重新建立 Mate 到出现项的关系，不反查场景、Worker 或名称别名。
   * @param tree 新树。
   * @param nodeMap 新元数据。
   */
  private rebuild(
    tree: readonly TreeOption[],
    nodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>,
  ): void {
    this.clear();
    this.tree = tree;
    this.nodeMap = nodeMap;
    for (const [key, metadata] of nodeMap) {
      if (
        metadata.kind !== FeatureTreeNodeKind.AssemblyMate ||
        !metadata.mateId
      )
        continue;
      const node = this.nodeIndex.resolve(tree, key);
      if (!node) continue;
      let bindings = this.bindingsByMateId.get(metadata.mateId);
      if (!bindings) {
        bindings = [];
        this.bindingsByMateId.set(metadata.mateId, bindings);
      }
      bindings.push({ node, metadata });
      this.nodes++;
    }
    this.builds++;
  }
}
